'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createCsvBlobFromRows,
  createDocxBlobFromTextSections,
  createWorkbookBlobFromRows,
} from '@/lib/browser-office-tools';
import {
  createCleanorExportFileName,
  extractPdfPageTexts,
  formatFileSize,
  isPdfFile,
  parsePageRanges,
} from '@/lib/browser-pdf-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  readPdfSource,
  revokePdfSource,
  withPdfDocument,
  type PdfToolSource,
} from '@/components/tools/pdf-tool-shared';

export type PdfToOfficeVariant = 'pdf-to-word' | 'pdf-to-excel';

type PageMode = 'all-pages' | 'custom';
type WordExportMode = 'structured' | 'text-first';
type SpreadsheetExportMode = 'auto' | 'xlsx' | 'csv';

type ToolCopy = {
  title: string;
  subtitle: string;
  badge: string;
  buttonLabel: string;
  processingLabel: string;
  successLabel: string;
};

const toolCopy: Record<PdfToOfficeVariant, ToolCopy> = {
  'pdf-to-word': {
    title: 'Convert text-based PDFs to DOCX',
    subtitle:
      'Extract selectable text, keep practical paragraphs and headings when possible, and export a DOCX locally.',
    badge: 'DOCX',
    buttonLabel: 'Export DOCX',
    processingLabel: 'Building DOCX…',
    successLabel: 'PDF-to-Word conversion',
  },
  'pdf-to-excel': {
    title: 'Convert table-like PDFs to Excel or CSV',
    subtitle:
      'Use a table-first extraction path for statements, reports, and simple tabular PDF pages.',
    badge: 'XLSX',
    buttonLabel: 'Export spreadsheet',
    processingLabel: 'Extracting tables…',
    successLabel: 'PDF-to-Excel conversion',
  },
};

function buildStructuredSections(pageTexts: Array<{ pageNumber: number; text: string }>) {
  return pageTexts.map((page) => {
    const blocks = page.text
      .split(/\n{2,}/)
      .map((value) => value.trim())
      .filter(Boolean);
    const first = blocks[0] ?? '';
    const looksLikeHeading =
      first.length > 0 &&
      first.length <= 90 &&
      first.split(/\s+/).length <= 12 &&
      !/[.!?]$/.test(first);

    return {
      heading: looksLikeHeading ? first : `Page ${page.pageNumber}`,
      paragraphs: looksLikeHeading ? blocks.slice(1) : blocks,
    };
  });
}

function chunkTextAsParagraphs(pageTexts: Array<{ pageNumber: number; text: string }>) {
  return pageTexts.map((page) => ({
    heading: `Page ${page.pageNumber}`,
    paragraphs: page.text
      .split(/\n+/)
      .map((value) => value.trim())
      .filter(Boolean),
  }));
}

function groupRowsFromItems(items: Array<{ str: string; transform: number[] }>) {
  type PositionedCell = { text: string; x: number; y: number };
  const sorted = [...items]
    .filter((item) => (item.str || '').trim())
    .sort((left, right) => {
      const deltaY = right.transform[5] - left.transform[5];
      if (Math.abs(deltaY) > 3) {
        return deltaY;
      }
      return left.transform[4] - right.transform[4];
    });

  const rows: PositionedCell[][] = [];

  for (const item of sorted) {
    const y = item.transform[5];
    let targetIndex = -1;

    for (let index = 0; index < rows.length; index += 1) {
      const anchor = rows[index][0];
      if (anchor && Math.abs(anchor.y - y) <= 4) {
        targetIndex = index;
        break;
      }
    }

    const cell: PositionedCell = {
      text: item.str.trim(),
      x: item.transform[4],
      y,
    };

    if (targetIndex === -1) {
      rows.push([cell]);
    } else {
      rows[targetIndex].push(cell);
    }
  }

  return rows.map((row) =>
    [...row]
      .sort((left, right) => left.x - right.x)
      .reduce<string[]>((cells, entry, index, ordered) => {
        if (index === 0) {
          cells.push(entry.text);
          return cells;
        }
        const previous = ordered[index - 1];
        if (entry.x - previous.x > 90) {
          cells.push(entry.text);
        } else {
          cells[cells.length - 1] = `${cells[cells.length - 1]} ${entry.text}`.trim();
        }
        return cells;
      }, []),
  );
}

function normalizeSpreadsheetRows(rows: string[][]) {
  const maxColumns = Math.max(...rows.map((row) => row.length), 1);
  return rows
    .filter((row) => row.some((cell) => cell.trim()))
    .map((row) => Array.from({ length: maxColumns }, (_, index) => row[index] ?? ''));
}

export default function BrowserPdfToOffice({ variant }: { variant: PdfToOfficeVariant }) {
  const copy = toolCopy[variant];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [source, setSource] = useState<PdfToolSource | null>(null);
  const [pageMode, setPageMode] = useState<PageMode>('all-pages');
  const [customRanges, setCustomRanges] = useState('1-3');
  const [wordMode, setWordMode] = useState<WordExportMode>('structured');
  const [spreadsheetMode, setSpreadsheetMode] = useState<SpreadsheetExportMode>('auto');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [previewLines, setPreviewLines] = useState<string[]>([]);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);

  const plannedPages = useMemo(() => {
    if (!source) {
      return [] as number[];
    }

    try {
      return pageMode === 'all-pages'
        ? Array.from({ length: source.pageCount }, (_, index) => index + 1)
        : parsePageRanges(customRanges, source.pageCount);
    } catch {
      return [] as number[];
    }
  }, [customRanges, pageMode, source]);

  useEffect(() => () => revokePdfSource(source), [source]);

  function replaceSource(nextSource: PdfToolSource | null) {
    setSource((current) => {
      revokePdfSource(current);
      return nextSource;
    });
  }

  async function handleFileSelection(file: File | null) {
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setPreviewLines([]);
    setFallbackMessage(null);

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isPdfFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one PDF file.');
      return;
    }

    setIsLoading(true);
    try {
      replaceSource(await readPdfSource(file));
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This PDF could not be opened.');
    } finally {
      setIsLoading(false);
    }
  }

  function clearSource() {
    replaceSource(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setResultSize(null);
    setPreviewLines([]);
    setFallbackMessage(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function runExport() {
    if (!source) {
      setErrorMessage('Choose a PDF first.');
      return;
    }

    let pageNumbers: number[];
    try {
      pageNumbers =
        pageMode === 'all-pages'
          ? Array.from({ length: source.pageCount }, (_, index) => index + 1)
          : parsePageRanges(customRanges, source.pageCount);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Check the page range and try again.',
      );
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setFallbackMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another PDF conversion right now.',
      );

      const { blob, previewText, fallback, extension } = await withPdfDocument(
        source.file,
        undefined,
        async (document) => {
          const extracted = await extractPdfPageTexts(document, pageNumbers);
          const pages = extracted
            .map((entry) => ({
              pageNumber: entry.pageNumber,
              text: entry.text,
              items: entry.items.map((item) => ({
                str: item.str,
                transform: item.transform,
              })),
            }))
            .filter((entry) => entry.text.trim() || entry.items.length);

          if (!pages.length) {
            throw new Error(
              variant === 'pdf-to-word'
                ? 'No selectable text was found. Use OCR PDF to Text first if this is a scanned document.'
                : 'No selectable table text was found. Use OCR PDF to Text first if this PDF is scan-only.',
            );
          }

          if (variant === 'pdf-to-word') {
            const sections =
              wordMode === 'structured'
                ? buildStructuredSections(pages)
                : chunkTextAsParagraphs(pages);
            const docxBlob = await createDocxBlobFromTextSections(
              `${source.name.replace(/\.pdf$/i, '')} export`,
              sections,
            );
            return {
              blob: docxBlob,
              previewText: sections.flatMap((section) => section.paragraphs).slice(0, 8),
              fallback:
                wordMode === 'structured'
                  ? 'Switch to Text-first DOCX if the PDF layout is too inconsistent.'
                  : null,
              extension: 'docx' as const,
            };
          }

          const inferredRows = normalizeSpreadsheetRows(
            pages.flatMap((entry) => groupRowsFromItems(entry.items)),
          );
          const rowHasMultipleColumns = inferredRows.some(
            (row) => row.length > 1 && row[1]?.trim(),
          );
          const shouldUseCsv =
            spreadsheetMode === 'csv' || (spreadsheetMode === 'auto' && !rowHasMultipleColumns);
          const spreadsheetBlob = shouldUseCsv
            ? createCsvBlobFromRows(
                inferredRows.length
                  ? inferredRows
                  : pages.map((entry) => [entry.text.replace(/\n+/g, ' ').trim()]),
              )
            : createWorkbookBlobFromRows(inferredRows, 'PDF export');

          return {
            blob: spreadsheetBlob,
            previewText: inferredRows.slice(0, 6).map((row) => row.join(' | ')),
            fallback: shouldUseCsv
              ? 'A flatter text pattern was found, so CSV is the safer export for this PDF.'
              : null,
            extension: shouldUseCsv ? ('csv' as const) : ('xlsx' as const),
          };
        },
      );

      const fileUrl = URL.createObjectURL(blob);
      triggerDownload(
        fileUrl,
        createCleanorExportFileName(
          variant,
          [source.name.replace(/\.pdf$/i, ''), plannedPages.length || source.pageCount],
          extension,
        ),
      );
      setTimeout(() => URL.revokeObjectURL(fileUrl), 0);
      setResultSize(blob.size);
      setPreviewLines(previewText);
      setFallbackMessage(fallback);
      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The Office export could not be completed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="project-detail-card tool-live-card">
      <div className="tool-live-heading">
        <div>
          <h2>{copy.title}</h2>
          <p>{copy.subtitle}</p>
        </div>
        <span className="tool-live-badge">{copy.badge}</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.name : 'Choose one PDF or drop it here'}</strong>
              <span>
                {source ? formatFileSize(source.size) : 'Text-based PDFs work best in v1'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="application/pdf,.pdf"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-control-grid">
            <label className="tool-live-field">
              <span>Pages</span>
              <select
                onChange={(event) => setPageMode(event.target.value as PageMode)}
                value={pageMode}
              >
                <option value="all-pages">All pages</option>
                <option value="custom">Custom range</option>
              </select>
            </label>
            {pageMode === 'custom' ? (
              <label className="tool-live-field">
                <span>Page range</span>
                <input
                  onChange={(event) => setCustomRanges(event.target.value)}
                  placeholder="1-3,5"
                  type="text"
                  value={customRanges}
                />
              </label>
            ) : null}
            {variant === 'pdf-to-word' ? (
              <label className="tool-live-field">
                <span>DOCX mode</span>
                <select
                  onChange={(event) => setWordMode(event.target.value as WordExportMode)}
                  value={wordMode}
                >
                  <option value="structured">Structured DOCX</option>
                  <option value="text-first">Text-first DOCX</option>
                </select>
              </label>
            ) : (
              <label className="tool-live-field">
                <span>Spreadsheet export</span>
                <select
                  onChange={(event) =>
                    setSpreadsheetMode(event.target.value as SpreadsheetExportMode)
                  }
                  value={spreadsheetMode}
                >
                  <option value="auto">Auto</option>
                  <option value="xlsx">Force XLSX</option>
                  <option value="csv">Force CSV</option>
                </select>
              </label>
            )}
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isLoading || isProcessing}
              onClick={() => void runExport()}
              type="button"
            >
              {isProcessing ? copy.processingLabel : copy.buttonLabel}
            </button>
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear
              </button>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Conversion summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Pages</strong>
              <span>{plannedPages.length || 'All pages'}</span>
            </div>
            <div>
              <strong>Source</strong>
              <span>{source ? source.name : 'No PDF selected'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>
                {variant === 'pdf-to-word'
                  ? 'DOCX'
                  : spreadsheetMode === 'csv'
                    ? 'CSV'
                    : 'XLSX / CSV'}
              </span>
            </div>
            <div>
              <strong>Result</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>OCR fallback</strong>
            <span>
              If the PDF is scan-only, use OCR PDF to Text first. This converter is optimized for
              selectable text and practical structure extraction.
            </span>
          </div>
        </aside>
      </div>

      {previewLines.length ? (
        <div className="tool-live-preview">
          <h3>Extraction preview</h3>
          <div className="tool-live-results-grid">
            {previewLines.map((line, index) => (
              <article className="tool-result-card" key={`${line}-${index}`}>
                <span>{line}</span>
              </article>
            ))}
          </div>
          {fallbackMessage ? (
            <div className="tool-live-panel-note">
              <strong>Fallback applied</strong>
              <span>{fallbackMessage}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function triggerDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}
